import React, { Fragment } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { PencilSquareIcon, XMarkIcon } from '@heroicons/react/24/solid';


const EditCustomerModal = ({ showModal, closeModal, handleSubmit, formState, handleInputChange, agents }) => {

  if (!formState) {
    return null;
  }

  return (
    <Transition show={showModal} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeModal}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gradient-to-br from-gray-900/80 via-gray-900/70 to-gray-900/80 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-2xl transform overflow-hidden text-left align-middle shadow-2xl transition-all bg-white rounded-2xl">
            {/* Header with gradient background */}
            <div className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 px-8 py-6">
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/20 rounded-lg p-2 transition-all duration-200"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
              <Dialog.Title as="h3" className="text-3xl font-bold text-white flex items-center gap-3">
                <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                  <PencilSquareIcon className="h-8 w-8 text-white" />
                </div>
                Edit Customer
              </Dialog.Title>
              <p className="mt-2 text-indigo-100 text-sm">
                Update customer information below
              </p>
            </div>

            <div className="px-8 py-6">
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
                      First Name
                    </label>
                    <input
                      id="firstName"
                      name="firstName"
                      required
                      onChange={handleInputChange}
                      value={formState.firstName || ''}
                      placeholder="Enter first name"
                      className="block w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 hover:border-gray-400 bg-gray-50 focus:bg-white"
                    />
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
                      Last Name
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      required
                      onChange={handleInputChange}
                      value={formState.lastName || ''}
                      placeholder="Enter last name"
                      className="block w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 hover:border-gray-400 bg-gray-50 focus:bg-white"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      onChange={handleInputChange}
                      value={formState.email || ''}
                      placeholder="Enter email address"
                      className="block w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 hover:border-gray-400 bg-gray-50 focus:bg-white"
                    />
                  </div>

                  <div>
                    <label htmlFor="phoneNumber" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      id="phoneNumber"
                      name="phoneNumber"
                      required
                      onChange={handleInputChange}
                      value={formState.phoneNumber || ''}
                      placeholder="Enter phone number"
                      className="block w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 hover:border-gray-400 bg-gray-50 focus:bg-white"
                    />
                  </div>

                  <div>
                    <label htmlFor="dateOfBirth" className="block text-sm font-semibold text-gray-700 mb-2">
                      Date of Birth
                    </label>
                    <input
                      id="dateOfBirth"
                      type="date"
                      name="dateOfBirth"
                      value={formState.dateOfBirth ? formState.dateOfBirth.split('T')[0] : ''}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 hover:border-gray-400 bg-gray-50 focus:bg-white"
                    />
                  </div>

                  <div>
                    <label htmlFor="customerType" className="block text-sm font-semibold text-gray-700 mb-2">
                      Customer Type
                    </label>
                    <select
                      id="customerType"
                      name="customerType"
                      value={formState.customerType || ''}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 hover:border-gray-400 bg-gray-50 focus:bg-white"
                    >
                      <option value="Private">Private</option>
                      <option value="Corporate">Corporate</option>
                    </select>
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">
                    Address
                  </label>
                  <input
                    id="address"
                    name="address"
                    required
                    onChange={handleInputChange}
                    value={formState.address || ''}
                    placeholder="Enter full address"
                    className="block w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 hover:border-gray-400 bg-gray-50 focus:bg-white"
                  />
                </div>


                {/* Action Buttons */}
                <div className="flex gap-4 pt-6 mt-6 border-t border-gray-200">
                  <button
                    type="submit"
                    className="flex-1 relative px-6 py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl
                    hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                    transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl"
                  >
                    <span className="relative z-10">Save Changes</span>
                  </button>

                  <button
                    type="button"
                    className="px-6 py-3.5 bg-gray-100 text-gray-700 font-semibold rounded-xl
                    hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400
                    transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EditCustomerModal;
